import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { KeycloakUser } from '../../models/keycloak-user.model';
import { Organization } from '../../models/organization.model';
import { KeycloakUserService } from '../../services/keycloak-user.service';
import { OrganizationService } from '../../services/organization.service';

@Component({
  selector: 'app-administration',
  imports: [CommonModule, FormsModule],
  templateUrl: './administration.html',
  styleUrl: './administration.scss'
})
export class AdministrationComponent implements OnInit {
  private organizationService = inject(OrganizationService);
  private keycloakUserService = inject(KeycloakUserService);

  organizations = signal<Organization[]>([]);
  users = signal<KeycloakUser[]>([]);
  loading = signal(false);
  usersLoading = signal(false);
  saving = signal(false);
  error = signal<string | null>(null);

  organizationName = signal('');
  organizationDescription = signal('');

  editingOrganizationId = signal<number | null>(null);
  editingName = signal('');
  editingDescription = signal('');

  usersModalOpen = signal(false);
  selectedOrganization = signal<Organization | null>(null);
  selectedUserIds = signal<string[]>([]);
  deleteModalOpen = signal(false);
  organizationToDelete = signal<Organization | null>(null);

  ngOnInit(): void {
    this.loadOrganizations();
    this.loadUsers();
  }

  loadOrganizations(): void {
    this.loading.set(true);
    this.error.set(null);

    this.organizationService.getAll().subscribe({
      next: (organizations) => {
        this.organizations.set(organizations);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load organizations', err);
        this.error.set('Failed to load organizations.');
        this.loading.set(false);
      }
    });
  }

  loadUsers(): void {
    this.usersLoading.set(true);

    this.keycloakUserService.getAll().subscribe({
      next: (users) => {
        this.users.set(users);
        this.usersLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load Keycloak users', err);
        this.error.set('Failed to load Keycloak users.');
        this.usersLoading.set(false);
      }
    });
  }

  createOrganization(): void {
    const name = this.organizationName().trim();

    if (!name || this.saving()) {
      this.error.set('Organization name is required.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    this.organizationService.create({
      name,
      description: this.organizationDescription().trim() || null
    }).subscribe({
      next: (organization) => {
        this.organizations.update(current => [...current, organization]);
        this.organizationName.set('');
        this.organizationDescription.set('');
        this.saving.set(false);
      },
      error: (err) => {
        console.error('Failed to create organization', err);
        this.error.set('Failed to create organization.');
        this.saving.set(false);
      }
    });
  }

  startEdit(organization: Organization): void {
    this.editingOrganizationId.set(organization.id);
    this.editingName.set(organization.name);
    this.editingDescription.set(organization.description ?? '');
  }

  cancelEdit(): void {
    this.editingOrganizationId.set(null);
    this.editingName.set('');
    this.editingDescription.set('');
  }

  saveEdit(organization: Organization): void {
    const name = this.editingName().trim();

    if (!name || this.saving()) {
      this.error.set('Organization name is required.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    this.organizationService.update(organization.id, {
      name,
      description: this.editingDescription().trim() || null
    }).subscribe({
      next: (updated) => {
        this.organizations.update(current =>
          current.map(item => item.id === updated.id ? updated : item)
        );
        this.cancelEdit();
        this.saving.set(false);
      },
      error: (err) => {
        console.error('Failed to update organization', err);
        this.error.set('Failed to update organization.');
        this.saving.set(false);
      }
    });
  }

  deleteOrganization(organization: Organization): void {
    if (this.saving()) {
      return;
    }

    this.organizationToDelete.set(organization);
    this.deleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    if (this.saving()) {
      return;
    }

    this.deleteModalOpen.set(false);
    this.organizationToDelete.set(null);
  }

  confirmDeleteOrganization(): void {
    const organization = this.organizationToDelete();

    if (!organization || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    this.organizationService.delete(organization.id).subscribe({
      next: () => {
        this.organizations.update(current => current.filter(item => item.id !== organization.id));
        this.saving.set(false);
        this.deleteModalOpen.set(false);
        this.organizationToDelete.set(null);
      },
      error: (err) => {
        console.error('Failed to delete organization', err);
        this.error.set('Failed to delete organization.');
        this.saving.set(false);
      }
    });
  }

  openUsersModal(organization: Organization): void {
    this.selectedOrganization.set(organization);
    this.selectedUserIds.set([]);
    this.usersModalOpen.set(true);
  }

  closeUsersModal(): void {
    this.usersModalOpen.set(false);
    this.selectedOrganization.set(null);
    this.selectedUserIds.set([]);
  }

  assignedUsers = computed(() => this.selectedOrganization()?.users ?? []);

  availableUsers = computed(() => {
    const assignedIds = new Set(this.assignedUsers().map(user => user.id));
    return this.users().filter(user => !assignedIds.has(user.id));
  });

  toggleUserSelection(userId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.selectedUserIds.update(current => {
      if (checked) {
        return current.includes(userId) ? current : [...current, userId];
      }

      return current.filter(id => id !== userId);
    });
  }

  isUserSelected(userId: string): boolean {
    return this.selectedUserIds().includes(userId);
  }

  addSelectedUsers(): void {
    const organization = this.selectedOrganization();

    if (!organization || this.saving()) {
      return;
    }

    if (!this.selectedUserIds().length) {
      this.error.set('Select at least one user.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    this.organizationService.addUsers(organization.id, {
      userIds: this.selectedUserIds()
    }).subscribe({
      next: (updated) => {
        this.replaceOrganization(updated);
        this.selectedOrganization.set(updated);
        this.selectedUserIds.set([]);
        this.saving.set(false);
      },
      error: (err) => {
        console.error('Failed to add users to organization', err);
        this.error.set('Failed to add users to organization.');
        this.saving.set(false);
      }
    });
  }

  removeUser(user: KeycloakUser): void {
    const organization = this.selectedOrganization();

    if (!organization || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    this.organizationService.removeUser(organization.id, user.id).subscribe({
      next: () => {
        const updated: Organization = {
          ...organization,
          users: organization.users.filter(item => item.id !== user.id)
        };

        this.replaceOrganization(updated);
        this.selectedOrganization.set(updated);
        this.saving.set(false);
      },
      error: (err) => {
        console.error('Failed to remove user from organization', err);
        this.error.set('Failed to remove user from organization.');
        this.saving.set(false);
      }
    });
  }

  getUserLabel(user: KeycloakUser): string {
    const details = [user.firstName, user.lastName].filter(Boolean).join(' ');

    if (details && user.email) {
      return `${user.username} - ${details} - ${user.email}`;
    }

    if (details) {
      return `${user.username} - ${details}`;
    }

    if (user.email) {
      return `${user.username} - ${user.email}`;
    }

    return user.username;
  }

  private replaceOrganization(organization: Organization): void {
    this.organizations.update(current =>
      current.map(item => item.id === organization.id ? organization : item)
    );
  }
}
